import React, { useState, useEffect } from "react";
import { Text, Flex, Button, Icon, Spinner } from "@chakra-ui/react";

import {
  backgroundLight,
  primaryAlt,
  textSecondary,
  white,
} from "../../../../utils/colors";
import { useTranslation } from "react-i18next";
import useThemeColors from "../../../../hooks/useThemeColors";
import { FaCircleInfo } from "react-icons/fa6";
import dayjs from "dayjs";
import { useSelector, useDispatch } from "react-redux";
import { listUsersEmployees } from "../../../../actions/userActions"; // Action to fetch employees

// Dynamically set locale based on local storage value
const locale = localStorage.getItem("language") || "en"; // Default to 'en' if not set
dayjs.locale(locale);

const ActiveFilter = ({
  filterParams,
  setIsFilterActive,
  onFilterApply,
  onReset,
}) => {
  const { t } = useTranslation();
  const { textPrimaryMode } = useThemeColors();

  const dispatch = useDispatch();

  const [coordinatorNames, setCoordinatorNames] = useState({});

  // Fetch coordinators from Redux store
  const userEmployeesList = useSelector((state) => state.userEmployeesList);
  const { loading, employees } = userEmployeesList;

  useEffect(() => {
    dispatch(listUsersEmployees()); // Fetch employees list
  }, [dispatch]);

  useEffect(() => {
    if (employees && employees.length > 0) {
      // Convert array of employees to an object { id: "First Last", ... }
      const coordinatorMap = employees.reduce((acc, employee) => {
        acc[employee._id] = `${employee.firstName} ${employee.lastName}`;
        return acc;
      }, {});

      setCoordinatorNames(coordinatorMap);
    }
  }, [employees]);

  console.log("filterParams", filterParams);
  const displayAgeRange = (minAge, maxAge) => {
    // Check if both minAge and maxAge are null (representing no specific range)
    if (minAge === null && maxAge === null) {
      console.log("filter.allAges");
      return t("filter.allAges"); // All ages if no specific range is set
    }
    // Check if only minAge is set
    else if (minAge !== null && maxAge === null) {
      console.log("filter.fromAge");
      return t("filter.fromAge", { age: minAge }); // Only minAge set
    }
    // Check if only maxAge is set
    else if (minAge === null && maxAge !== null) {
      console.log("filter.upToAge");
      return t("filter.upToAge", { age: maxAge }); // Only maxAge set
    }
    // If both minAge and maxAge are set to specific values, log and return range
    else {
      console.log("filter.fromToAge");
      return t("filter.fromToAge", { min: minAge, max: maxAge }); // Both minAge and maxAge set
    }
  };

  // Helper function to display status as a string
  const displayStatus = (status) => {
    // Ensure status is always an array
    if (typeof status === "string") {
      status = status.split(",").map((s) => s.trim());
    }

    if (!Array.isArray(status) || status.length === 0) {
      return ""; // No status selected
    }

    return status
      .map((s) => t(`treatmentPlanStatus.${s.trim()}`)) // Ensure translation key matches and trims any whitespace
      .join(", ");
  };

  // Convert coordinator IDs to names
  const displayCoordinator = (coordinatorId) => {
    if (!coordinatorId) return "";
    const ids = coordinatorId.split(","); // Convert string to array
    return ids
      .map((id) => coordinatorNames[id] || id) // Show name if found, otherwise show ID
      .join(", ");
  };

  const displayEmployeesCreated = (creatorEmployeeId) => {
    if (!creatorEmployeeId) return "";
    const ids = creatorEmployeeId.split(","); // Convert string to array
    return ids
      .map((id) => coordinatorNames[id] || id) // Show name if found, otherwise show ID
      .join(", ");
  };

  // Helper function to display state as a string
  // const displayState = (state) => {
  //   if (!state || state.length === 0) {
  //     return t("filter.none"); // No state selected
  //   }
  //   return state;
  // };
  // Helper function to display state as a string
  const displayState = (state) => {
    // Ensure state is always an array
    if (typeof state === "string") {
      state = state.split(",").map((s) => s.trim());
    }

    if (!Array.isArray(state) || state.length === 0) {
      return t("filter.none"); // No state selected
    }

    return state
      .map((s) => s.trim()) // Ensure translation key matches and trims any whitespace
      .join(", ");
  };

  // Helper function to format and display dates using dayjs
  const displayDateRange = (startDate, endDate) => {
    if (!startDate && !endDate) {
      return ""; // No dates selected
    } else if (startDate && !endDate) {
      return t("filter.singleDate", {
        date: dayjs(startDate).format("LL"), // Uses dayjs locale format
      });
    } else if (!startDate && endDate) {
      return t("filter.singleDate", {
        date: dayjs(endDate).format("LL"), // Uses dayjs locale format
      });
    } else if (
      startDate &&
      endDate &&
      dayjs(startDate).isSame(dayjs(endDate), "day")
    ) {
      // If start and end dates are the same, show a single date
      return t("filter.singleDate", {
        date: dayjs(startDate).format("LL"),
      });
    } else {
      return t("filter.dateRange", {
        start: dayjs(startDate).format("LL"),
        end: dayjs(endDate).format("LL"),
      });
    }
  };

  const clearFilter = () => {
    console.log("RESET IN ACTIVEFILTER PRESSED");

    const resetState = {
      minPrice: null,
      maxPrice: null,
      startDate: "",
      endDate: "",
      status: [],
      employeeId: [],
      coordinatorId: [],
    };

    // Apply the reset state using the onFilterApply function passed as a prop
    onFilterApply(resetState);

    // Trigger the reset in PatientFilterPopover through the prop
    if (typeof onReset === "function") {
      onReset();
    }

    // Update the filter active state
    setIsFilterActive(false);

    // Save the reset state in local storage
    localStorage.setItem("filterSettings_patients", JSON.stringify(resetState));
  };

  // Logs
  //   console.log("Filter Params:", filterParams);

  return (
    <>
      <Flex
        w="100%"
        align="center"
        justify="space-between"
        p={["0rem 1.5rem 1rem", "0rem 2rem 1rem"]}
        direction={["column", "row"]}
      >
        <Flex align="flex-start" w="100%" direction={["column", "row"]}>
          <Flex align="center">
            <Icon
              as={FaCircleInfo}
              fontSize="0.875rem"
              color={primaryAlt}
              position="relative"
              top="-1px"
            />
            <Text
              fontSize="0.8rem"
              color={textSecondary}
              fontWeight="500"
              ml={"0.5rem"}
              whiteSpace="nowrap"
            >
              {t("filter.filterActive")}.
            </Text>
          </Flex>

          <Flex align="center" gap={1} m={["1rem 0 0", "initial"]}>
            <Text
              fontSize="0.8rem"
              color={textSecondary}
              fontWeight="500"
              ml={["0rem", "0.25rem"]}
              pr="2rem"
            >
              {t("filter.showingTreatmentPlans")}

              {(() => {
                let first = true; // Keeps track of whether it's the first filter applied

                const addComma = () => {
                  if (!first) return ", "; // Add a comma only if it's NOT the first filter
                  first = false;
                  return "";
                };
                return (
                  <>
                    {filterParams.status && filterParams.status.length > 0 && (
                      <>
                        <Text
                          as="span"
                          fontSize="0.8rem"
                          color={textSecondary}
                          fontWeight="500"
                          mr={1}
                        >
                          {addComma()}
                          {t("filter.withStatus")}
                        </Text>

                        <Text
                          as="span"
                          fontSize="0.8rem"
                          color={textPrimaryMode}
                          fontWeight="500"
                          textTransform="lowercase"
                        >
                          {`${displayStatus(filterParams.status)}`}
                        </Text>
                      </>
                    )}

                    {/* COORDINATORS */}
                    {filterParams.coordinatorId &&
                      filterParams.coordinatorId.length > 0 && (
                        <>
                          <Text
                            as="span"
                            color={textSecondary}
                            fontWeight="500"
                          >
                            {addComma()}
                            {t("filter.withCoordinator")}
                          </Text>
                          {loading ? (
                            <Spinner size="xs" />
                          ) : (
                            <Text
                              as="span"
                              color={textPrimaryMode}
                              fontWeight="500"
                            >
                              {displayCoordinator(filterParams.coordinatorId)}
                            </Text>
                          )}
                        </>
                      )}

                    {/* EMPLOYEES */}
                    {filterParams.creatorEmployeeId &&
                      filterParams.creatorEmployeeId.length > 0 && (
                        <>
                          <Text
                            as="span"
                            color={textSecondary}
                            fontWeight="500"
                          >
                            {addComma()}
                            {t("filter.withCreatorEmployee")}
                          </Text>
                          {loading ? (
                            <Spinner size="xs" />
                          ) : (
                            <Text
                              as="span"
                              color={textPrimaryMode}
                              fontWeight="500"
                            >
                              {displayEmployeesCreated(
                                filterParams.creatorEmployeeId
                              )}
                            </Text>
                          )}
                        </>
                      )}

                    {filterParams.startDate && filterParams.endDate && (
                      <>
                        <Text
                          as="span"
                          fontSize="0.8rem"
                          color={textSecondary}
                          fontWeight="500"
                          mr={1}
                        >
                          {addComma()}
                          {t("filter.creationDate")}
                        </Text>
                        <Text
                          as="span"
                          fontSize="0.8rem"
                          color={textPrimaryMode}
                          fontWeight="500"
                        >
                          {`${displayDateRange(
                            filterParams.startDate,
                            filterParams.endDate
                          )}`}
                        </Text>
                      </>
                    )}

                    <>
                      <Text
                        as="span"
                        fontSize="0.8rem"
                        color={textSecondary}
                        fontWeight="500"
                        mr={1}
                      >
                        {addComma()}
                        {t("filter.price")}
                      </Text>

                      <Text
                        as="span"
                        fontSize="0.8rem"
                        color={textPrimaryMode}
                        fontWeight="500"
                      >
                        {`${displayAgeRange(
                          filterParams.minPrice,
                          filterParams.maxPrice
                        )}`}
                      </Text>
                    </>

                    {filterParams.state && filterParams.state.length > 0 && (
                      <>
                        <Text
                          as="span"
                          fontSize="0.8rem"
                          color={textSecondary}
                          fontWeight="500"
                          mr={1}
                        >
                          {addComma()}
                          {t("filter.andState")}
                        </Text>
                        <Text
                          as="span"
                          fontSize="0.8rem"
                          color={textPrimaryMode}
                          fontWeight="500"
                          // textTransform="lowercase"
                        >
                          {displayState(filterParams.state)}
                        </Text>
                      </>
                    )}
                  </>
                );
              })()}
            </Text>

            {/* STATUS START */}
            {/* {filterParams &&
            filterParams.status &&
            filterParams.status.length > 0 ? (
              <Flex align="center">
                <Text
                  fontSize="0.8rem"
                  color={textSecondary}
                  fontWeight="500"
                  lineHeight="1.4"
                  mr={1}
                >
                  {t("filter.withStatus")}
                </Text>
                <Text
                  fontSize="0.8rem"
                  color={textPrimaryMode}
                  fontWeight="500"
                  lineHeight="1.4"
                  textTransform="lowercase"
                >
                  {displayStatus(filterParams.status)}
                </Text>
              </Flex>
            ) : null} */}
            {/* STATUS END */}

            {/* DATE START */}
            {/* {filterParams && filterParams.startDate && filterParams.endDate ? (
              <Flex align="center">
                <Text
                  fontSize="0.8rem"
                  color={textSecondary}
                  fontWeight="500"
                  lineHeight="1.4"
                  mr={1}
                >
                  {t("filter.date")}
                </Text>
                <Text
                  fontSize="0.8rem"
                  color={textPrimaryMode}
                  fontWeight="500"
                  lineHeight="1.4"
                >
                  {displayDateRange(
                    filterParams.startDate,
                    filterParams.endDate
                  )}
                </Text>
              </Flex>
            ) : null} */}

            {/* DATE END  */}

            {/* AGE START */}
            {/* {(filterParams.minAge !== 0 || filterParams.maxAge !== 100) && (
              <Flex align="center">
                <Text
                  fontSize="0.8rem"
                  color={textSecondary}
                  fontWeight="500"
                  lineHeight="1.4"
                  mr={1}
                >
                  {t("filter.age")}
                </Text>
                <Text
                  fontSize="0.8rem"
                  color={textPrimaryMode}
                  fontWeight="500"
                  lineHeight="1.4"
                >
                  {displayAgeRange(filterParams.minAge, filterParams.maxAge)}
                </Text>
              </Flex>
            )} */}

            {/* AGE END  */}

            {/* STATE START  */}
            {/* {filterParams &&
              filterParams.state &&
              filterParams.state.length > 0 && (
                <Flex align="center">
                  <Text
                    fontSize="0.8rem"
                    color={textSecondary}
                    fontWeight="500"
                    lineHeight="1.4"
                    mr={1}
                  >
                    {t("filter.andState")}
                  </Text>
                  <Text
                    fontSize="0.8rem"
                    color={textPrimaryMode}
                    fontWeight="500"
                    lineHeight="1.4"
                  >
                    {displayState(filterParams.state)}
                  </Text>
                </Flex>
              )} */}

            {/* STATE END  */}
          </Flex>
        </Flex>

        <Flex
          align="center"
          justify={["flex-start", "flex-end"]}
          w={["100%", "initial"]}
          m={["1rem 0 0", "initial"]}
        >
          <Button
            size="sm"
            fontSize="0.8rem"
            borderRadius="10rem"
            bg={backgroundLight}
            color={textSecondary}
            p="0.5rem 1rem"
            onClick={clearFilter}
            _hover={{ bg: primaryAlt, color: white }}
          >
            {t("filter.clearFilter")}
          </Button>
        </Flex>
      </Flex>
    </>
  );
};

export default ActiveFilter;
